<script setup lang="ts">
import { computed } from 'vue'
import { useAppStore } from '@/stores/app'
import { useResourcesStore } from '@/stores/resources'
import { useTr } from '@/composables/useTr'

const app = useAppStore()
const res = useResourcesStore()
const { tr } = useTr()

const assets = computed(() => res.viewAssets)

const bySev = (sev: string) => assets.value.filter((a) => a.findings > 0 && a.maxSev === sev).length
const noFindings = computed(() => assets.value.filter((a) => a.findings === 0).length)
const byLabel = (label: string) => assets.value.filter((a) => (a.labels || []).includes(label)).length

const labels = ['production', 'staging', 'internal', 'dev', 'api', 'cdn', 'ci/cd', 'blog', 'docs', 'marketing', 'monitoring']
</script>

<template>
  <aside class="inventory-sub-nav">
    <div class="ssn-header">{{ tr('按线索严重度', 'By Finding Severity') }}</div>
    <div class="ssn-item" :class="{ active: app.inventoryFilter === 'all' }" @click="app.inventoryFilter = 'all'">
      <span class="ssn-icon"><svg fill="none" stroke="currentColor" stroke-width="1.8" viewBox="0 0 24 24"><circle cx="12" cy="12" r="9" /></svg></span>
      <span style="flex:1;">{{ tr('全部资产', 'All Assets') }}</span>
      <span class="badge num">{{ assets.length }}</span>
    </div>
    <div class="ssn-item" :class="{ active: app.inventoryFilter === 'crit' }" @click="app.inventoryFilter = 'crit'">
      <span style="display:inline-block; width:8px; height:8px; border-radius:50%; background:var(--sev-crit); margin-right:2px;" />
      <span style="flex:1;">{{ tr('有 Critical', 'Has Critical') }}</span>
      <span class="badge num">{{ bySev('crit') }}</span>
    </div>
    <div class="ssn-item" :class="{ active: app.inventoryFilter === 'high' }" @click="app.inventoryFilter = 'high'">
      <span style="display:inline-block; width:8px; height:8px; border-radius:50%; background:var(--sev-high); margin-right:2px;" />
      <span style="flex:1;">{{ tr('有 High', 'Has High') }}</span>
      <span class="badge num">{{ bySev('high') }}</span>
    </div>
    <div class="ssn-item" :class="{ active: app.inventoryFilter === 'med' }" @click="app.inventoryFilter = 'med'">
      <span style="display:inline-block; width:8px; height:8px; border-radius:50%; background:var(--sev-med); margin-right:2px;" />
      <span style="flex:1;">{{ tr('有 Medium', 'Has Medium') }}</span>
      <span class="badge num">{{ bySev('med') }}</span>
    </div>
    <div class="ssn-item" :class="{ active: app.inventoryFilter === 'low' }" @click="app.inventoryFilter = 'low'">
      <span style="display:inline-block; width:8px; height:8px; border-radius:50%; background:var(--sev-low); margin-right:2px;" />
      <span style="flex:1;">{{ tr('仅 Low', 'Only Low') }}</span>
      <span class="badge num">{{ bySev('low') }}</span>
    </div>
    <div class="ssn-item" :class="{ active: app.inventoryFilter === 'nofindings' }" @click="app.inventoryFilter = 'nofindings'">
      <span class="ssn-icon"><svg fill="none" stroke="currentColor" stroke-width="1.8" viewBox="0 0 24 24"><polyline points="20 6 9 17 4 12" /></svg></span>
      <span style="flex:1;">{{ tr('无线索', 'No Findings') }}</span>
      <span class="badge num">{{ noFindings }}</span>
    </div>
    <div class="ssn-header" style="padding-top:14px;">{{ tr('按标签', 'By Tag') }}</div>
    <template v-for="label in labels" :key="label">
      <div
        v-show="byLabel(label) > 0"
        class="ssn-item"
        :class="{ active: app.inventoryFilter === label }"
        @click="app.inventoryFilter = label"
      >
        <span class="ssn-icon"><svg fill="none" stroke="currentColor" stroke-width="1.8" viewBox="0 0 24 24"><path d="M20.59 13.41l-7.17 7.17a2 2 0 01-2.83 0L2 12V2h10l8.59 8.59a2 2 0 010 2.82z" /><circle cx="7" cy="7" r="1.5" /></svg></span>
        <span style="flex:1;">{{ label }}</span>
        <span class="badge num">{{ byLabel(label) }}</span>
      </div>
    </template>
  </aside>
</template>
